/**
 * Thin RGB-module API client until `@nbr/api-types` is regenerated with events/deposits/account paths.
 * Uses the same base URL and bearer/cookie auth as the openapi-fetch client.
 */
import { apiV1BaseUrl } from "@/shared/config/env"
import { getAccessToken } from "@/shared/auth/tokenStore"
import { ApiError, refreshAccessToken } from "./client"
import type { Problem } from "./types"

// ------------------------------------------------------------------ types

export type PlatformPermissions = { permissions: string[] }

export type AccountProfile = {
  account_id: string
  bio?: string
  avatar_url?: string
  phone?: string
  country_code?: string
  city_id?: string
  city_name?: string
  links?: Array<Record<string, unknown>>
  creative?: Record<string, unknown>
  updated_at?: string
}

export type AccountProfileUpdate = {
  bio?: string
  avatar_url?: string
  phone?: string
  country_code?: string
  city_id?: string
  city_name?: string
  links?: Array<Record<string, unknown>>
  creative?: Record<string, unknown>
}

export type AccountEducation = {
  id: string
  institution: string
  specialty?: string
  degree?: string
  year_from?: number
  year_to?: number
  sort_order?: number
}

export type AccountEducationInput = {
  institution: string
  specialty?: string
  degree?: string
  year_from?: number
  year_to?: number
  sort_order?: number
}

export type AccountLanguage = {
  id: string
  code: string
  level: string
}

export type AccountLanguageInput = {
  code: string
  level: string
}

export type AccountWork = {
  id: string
  organization: string
  position?: string
  industry?: string
  year_from?: number
  year_to?: number
  sort_order?: number
}

export type AccountWorkInput = {
  organization: string
  position?: string
  industry?: string
  year_from?: number
  year_to?: number
  sort_order?: number
}

export type AccountPortfolioItem = {
  id: string
  title: string
  description?: string
  url?: string
  file_key?: string
  sort_order?: number
}

export type AccountPortfolioInput = {
  title: string
  description?: string
  url?: string
  file_key?: string
  sort_order?: number
}

export type NotificationItem = {
  id: string
  type: string
  severity: string
  title: string
  body: string
  payload?: Record<string, unknown>
  read_at?: string
  created_at: string
}

export type EventSummary = {
  id: string
  title: string
  status: "draft" | "pending_review" | "approved" | "rejected" | "cancelled"
  is_public: boolean
  organizer_id: string
  starts_at?: string
  ends_at?: string
  city?: string
  place?: string
  cover_url?: string
}

export type EventDetail = EventSummary & {
  description?: string
  industry?: string
  registration_opens_at?: string
  registration_closes_at?: string
  require_approval?: boolean
}

export type EventInput = {
  title: string
  description?: string
  starts_at?: string
  ends_at?: string
  city?: string
  place?: string
  industry?: string
  require_approval?: boolean
}

export type EventRegistration = {
  id: string
  event_id: string
  account_id: string
  status: string
  created_at: string
  updated_at?: string
}

export type DepositSummary = {
  id: string
  title: string
  industry: string
  status: "pending_moderation" | "team_review" | "confirmed" | "rejected" | "needs_revision" | "withdrawn"
  owner_id: string
  is_public?: boolean
  submitted_at?: string
}

export type DepositDetail = DepositSummary & {
  description?: string
  sha256_hex?: string
  content_type?: string
  size_bytes?: number
  original_name?: string
  certificate_url?: string
}

export type DepositDraft = {
  id: string
  account_id: string
  title?: string
  description?: string
  industry?: string
  step: number
  staged_file_id?: string
  updated_at?: string
}

export type DepositDraftInput = {
  title?: string
  description?: string
  industry?: string
  step?: number
}

export type AssociationKind = {
  id: string
  code: string
  title: string
  sort_order?: number
}

export type AssociationGroup = {
  id: string
  kind_id: string
  parent_id?: string | null
  title: string
  slug?: string
  description?: string
  requires_approval: boolean
}

export type DomainAuditEntry = {
  id: number
  entity_id: string
  actor_id?: string
  from_status?: string
  to_status: string
  note?: string
  created_at: string
}

export type Paged<T> = { items: T[]; total: number }
export type Listed<T> = { items: T[] }

// ------------------------------------------------------------------ fetch

const parseProblem = async (response: Response): Promise<Problem | undefined> => {
  try {
    const body = (await response.clone().json()) as unknown
    if (body && typeof body === "object" && "title" in body) return body as Problem
  } catch {
    /* ignore */
  }
  return undefined
}

export const rgbFetch = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const url = path.startsWith("http") ? path : `${apiV1BaseUrl}${path.startsWith("/") ? path : `/${path}`}`
  const headers = new Headers(init.headers)
  if (!headers.has("Accept")) headers.set("Accept", "application/json")
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json")

  const withAuth = async (token: string | null) => {
    const next = new Headers(headers)
    if (token) next.set("Authorization", `Bearer ${token}`)
    return fetch(url, { ...init, headers: next, credentials: "same-origin" })
  }

  let response = await withAuth(getAccessToken())
  if (response.status === 401) {
    try {
      const token = await refreshAccessToken()
      response = await withAuth(token)
    } catch {
      /* keep original 401 */
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, url, await parseProblem(response), response.statusText)
  }
  if (response.status === 204) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

const qs = (params?: Record<string, string | number | undefined>) => {
  if (!params) return ""
  const sp = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === "") return
    sp.set(key, String(value))
  })
  const s = sp.toString()
  return s ? `?${s}` : ""
}

// ------------------------------------------------------------------ permissions

export const getPlatformPermissions = () => rgbFetch<PlatformPermissions>("/platform/permissions")

// ------------------------------------------------------------------ account

export const getAccountProfile = () => rgbFetch<AccountProfile>("/account/profile")
export const updateAccountProfile = (body: AccountProfileUpdate) =>
  rgbFetch<AccountProfile>("/account/profile", { method: "PUT", body: JSON.stringify(body) })

export const listAccountEducation = () => rgbFetch<Listed<AccountEducation>>("/account/education")
export const createAccountEducation = (body: AccountEducationInput) =>
  rgbFetch<AccountEducation>("/account/education", { method: "POST", body: JSON.stringify(body) })
export const deleteAccountEducation = (id: string) =>
  rgbFetch<void>(`/account/education/${encodeURIComponent(id)}`, { method: "DELETE" })

export const listAccountLanguages = () => rgbFetch<Listed<AccountLanguage>>("/account/languages")
export const upsertAccountLanguage = (body: AccountLanguageInput) =>
  rgbFetch<AccountLanguage>("/account/languages", { method: "POST", body: JSON.stringify(body) })
export const deleteAccountLanguage = (id: string) =>
  rgbFetch<void>(`/account/languages/${encodeURIComponent(id)}`, { method: "DELETE" })

export const listAccountWork = () => rgbFetch<Listed<AccountWork>>("/account/work")
export const createAccountWork = (body: AccountWorkInput) =>
  rgbFetch<AccountWork>("/account/work", { method: "POST", body: JSON.stringify(body) })
export const deleteAccountWork = (id: string) =>
  rgbFetch<void>(`/account/work/${encodeURIComponent(id)}`, { method: "DELETE" })

export const listAccountPortfolio = () => rgbFetch<Listed<AccountPortfolioItem>>("/account/portfolio")
export const createAccountPortfolio = (body: AccountPortfolioInput) =>
  rgbFetch<AccountPortfolioItem>("/account/portfolio", { method: "POST", body: JSON.stringify(body) })
export const deleteAccountPortfolio = (id: string) =>
  rgbFetch<void>(`/account/portfolio/${encodeURIComponent(id)}`, { method: "DELETE" })

// ------------------------------------------------------------------ notifications

export const listNotifications = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<{ items: NotificationItem[]; total: number; unread: number }>(`/notifications${qs(params)}`)

export const markNotificationRead = (id: string) =>
  rgbFetch<NotificationItem>(`/notifications/${encodeURIComponent(id)}/read`, { method: "POST" })

// ------------------------------------------------------------------ events

export const listPublicEvents = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<EventSummary>>(`/events${qs(params)}`)

export const getEvent = (id: string) => rgbFetch<EventDetail>(`/events/${encodeURIComponent(id)}`)

export const createEvent = (body: EventInput) =>
  rgbFetch<EventDetail>("/events", { method: "POST", body: JSON.stringify(body) })

export const listMyEvents = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<EventSummary>>(`/events/mine${qs(params)}`)

export const listEventsModeration = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<EventSummary>>(`/events/moderation${qs(params)}`)

export const registerForEvent = (id: string) =>
  rgbFetch<EventRegistration>(`/events/${encodeURIComponent(id)}/registrations`, {
    method: "POST",
    body: JSON.stringify({}),
  })

export const listMyRegistrations = () => rgbFetch<Listed<EventRegistration>>("/events/registrations/mine")

export const submitEvent = (id: string) =>
  rgbFetch<EventDetail>(`/events/${encodeURIComponent(id)}/submit`, { method: "POST" })

export const moderateEvent = (id: string, decision: "approve" | "reject", note?: string) =>
  rgbFetch<EventDetail>(`/events/${encodeURIComponent(id)}/moderate`, {
    method: "POST",
    body: JSON.stringify({ decision, note, is_public: true }),
  })

// ------------------------------------------------------------------ deposits

export const listPublicDeposits = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<DepositSummary>>(`/deposits${qs(params)}`)

export const getDeposit = (id: string) => rgbFetch<DepositDetail>(`/deposits/${encodeURIComponent(id)}`)

export const listMyDeposits = () => rgbFetch<Listed<DepositSummary>>("/deposits/mine")

export const listDepositsModeration = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<DepositSummary>>(`/deposits/moderation${qs(params)}`)

export const listDepositDrafts = () => rgbFetch<Listed<DepositDraft>>("/deposits/drafts")

export const createDepositDraft = (body: DepositDraftInput = {}) =>
  rgbFetch<DepositDraft>("/deposits/drafts", { method: "POST", body: JSON.stringify(body) })

export const updateDepositDraft = (id: string, body: DepositDraftInput) =>
  rgbFetch<DepositDraft>(`/deposits/drafts/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  })

export const submitDepositDraft = (id: string) =>
  rgbFetch<DepositDetail>(`/deposits/drafts/${encodeURIComponent(id)}/submit`, { method: "POST" })

export const moderateDeposit = (
  id: string,
  decision: "approve" | "reject" | "request_revision",
  note?: string,
) =>
  rgbFetch<DepositDetail>(`/deposits/${encodeURIComponent(id)}/moderate`, {
    method: "POST",
    body: JSON.stringify({ decision, note }),
  })

// ------------------------------------------------------------------ associations / admin audit

export const listAssociationKinds = () => rgbFetch<Listed<AssociationKind>>("/associations/kinds")
export const listAssociationGroups = () => rgbFetch<Listed<AssociationGroup>>("/associations/groups")

export const listEventAudit = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<DomainAuditEntry>>(`/admin/audit/events${qs(params)}`)

export const listDepositAudit = (params?: { page?: number; page_size?: number }) =>
  rgbFetch<Paged<DomainAuditEntry>>(`/admin/audit/deposits${qs(params)}`)

export const listEventRegistrations = (eventId: string) =>
  rgbFetch<Listed<EventRegistration>>(`/events/${encodeURIComponent(eventId)}/registrations`)

export const checkInRegistration = (registrationId: string) =>
  rgbFetch<EventRegistration>(`/events/registrations/${encodeURIComponent(registrationId)}/check-in`, {
    method: "POST",
  })

export const checkOutRegistration = (registrationId: string) =>
  rgbFetch<EventRegistration>(`/events/registrations/${encodeURIComponent(registrationId)}/check-out`, {
    method: "POST",
  })

export type CreativeRoleDefinition = {
  id: string
  direction: string
  subdirection: string
  role_name: string
  description?: string
}

export const listCreativeRoles = () => rgbFetch<Listed<CreativeRoleDefinition>>("/account/creative-roles")
